import {
  MarkerLabelFieldTypeEnum,
  PointOfInterest,
  UnitsEnum,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import {
  BLISTER_HEIGHT_LABEL,
  BLISTER_TYPE,
  NOT_APPLICABLE_LABEL,
  PAINT_BLOCK_LABEL,
  PIT_DEPTH_LABEL,
  UNCERTAIN_PROFILE_LABEL,
} from '@/constants';
import { formatPercentage, localiseMeasurement } from '@/utils/unitSystem';

// ---------------------------------------------------------------------------
// Blister field formatting helpers
// ---------------------------------------------------------------------------

const formatMm = (
  value: number | null | undefined,
  unitSystem: UnitSystemEnum,
  fallback = NOT_APPLICABLE_LABEL
) =>
  localiseMeasurement({
    unitSystem,
    value: value || undefined,
    sourceUnit: UnitsEnum.Meter,
    displayAsUnit: UnitsEnum.Millimeter,
    showUnitLabel: true,
  }) ?? fallback;

const formatMmWithPercentage = (
  fieldData: { value?: number | null; percentage?: number | null } | null | undefined,
  unitSystem: UnitSystemEnum
) => {
  if (fieldData?.value === null || fieldData?.value === undefined) {
    return NOT_APPLICABLE_LABEL;
  }
  return (
    formatMm(fieldData.value, unitSystem, '') +
    (fieldData.percentage !== null && fieldData.percentage !== undefined
      ? formatPercentage(fieldData.percentage)
      : '')
  );
};

const formatHeight = (
  value: number | null | undefined,
  unitSystem: UnitSystemEnum
) =>
  localiseMeasurement({
    unitSystem,
    value,
    sourceUnit: UnitsEnum.Meter,
    roundToDecimal: 1,
  }) ?? NOT_APPLICABLE_LABEL;

// ---------------------------------------------------------------------------
// Blister field categories — keys grouped by formatting strategy
// ---------------------------------------------------------------------------

const SIMPLE_MM_KEYS = new Set(['estimatedHeight', 'manualSetHeight', 'pdrHeight']);

const MM_PERCENTAGE_KEYS = new Set(['wallLoss', 'residualCorrosionAllowance']);

const STRING_PASSTHROUGH_KEYS = new Set([
  'heightType',
  'verifiedMaxHeight',
  'verifiedResidualThickness',
  'verifiedResidualCorrosionAllowance',
  'measurementConfidence',
  'distanceConfidence',
]);

// ---------------------------------------------------------------------------
// Default label maps
// ---------------------------------------------------------------------------

const DEFAULT_BLISTER_LABELS: Record<string, string> = {
  estimatedHeight: 'Estimated Height',
  manualSetHeight: 'Manual Set Height',
  blisterHeightIndex: 'Blister Height Index',
  wallLoss: 'Estimated Wall Loss',
  residualThickness: 'Residual Thickness',
  residualCorrosionAllowance: 'Residual Corrosion Allowance',
  pdrHeight: 'PDR Height',
  heightType: 'Calculation Method',
  verifiedMaxHeight: 'Verified Height',
  verifiedResidualThickness: 'Verified Residual Thickness',
  verifiedResidualCorrosionAllowance: 'Verified Residual Corrosion Allowance',
  measurementConfidence: 'Measurement Confidence',
  distanceConfidence: 'Distance Confidence',
};

const POI_LABELS: Record<string, string> = {
  deckRelativeHeight: 'Height from deck',
  paintRegion: PAINT_BLOCK_LABEL,
  deckName: 'Deck Name',
  status: 'Status',
  assembly: 'Component',
  assignee: 'Assigned To',
  visibility: 'Access Control',
  accessibility: 'Accessibility',
  viewpointRelativeHeight: 'POI Height from Viewpoint',
  createdBy: 'Created By',
};

// ---------------------------------------------------------------------------
// POI field value accessors
// ---------------------------------------------------------------------------

const POI_ACCESSORS: Record<
  string,
  (poi: PointOfInterest, unitSystem: UnitSystemEnum) => string
> = {
  deckRelativeHeight: (poi, us) => formatHeight(poi.deckRelativeHeight, us),
  paintRegion: (poi) => poi.paintRegion?.name ?? NOT_APPLICABLE_LABEL,
  deckName: (poi) => poi.paintRegion?.deck?.name ?? NOT_APPLICABLE_LABEL,
  status: (poi) => String(poi.status ?? NOT_APPLICABLE_LABEL),
  assembly: (poi) => poi.assembly?.tagName ?? NOT_APPLICABLE_LABEL,
  assignee: (poi) => poi.assignee ?? NOT_APPLICABLE_LABEL,
  visibility: (poi) => String(poi.visibility ?? NOT_APPLICABLE_LABEL),
  accessibility: (poi) => String(poi.accessibility ?? NOT_APPLICABLE_LABEL),
  viewpointRelativeHeight: (poi, us) =>
    formatHeight(poi.viewpointRelativeHeight, us),
  createdBy: (poi) => poi.createdByUser?.name ?? NOT_APPLICABLE_LABEL,
};

// ---------------------------------------------------------------------------
// Core resolver
// ---------------------------------------------------------------------------

type BlisterFieldConfig = Partial<
  Record<string, { name: string; hidden: boolean }>
>;

/**
 * Resolves a single review-panel field (Blister or POI) to a label + value.
 * Returns `null` when the field should be skipped (hidden, missing data, etc.).
 */
export const getReviewPanelFieldValue = (
  field: { key: string; type: MarkerLabelFieldTypeEnum },
  pointOfInterest: PointOfInterest,
  unitSystem: UnitSystemEnum,
  blisterFieldConfig?: BlisterFieldConfig
): { label: string; value: string } | null => {
  const { key, type } = field;

  // ----- Blister fields -----
  if (type === MarkerLabelFieldTypeEnum.Blister) {
    const blister = pointOfInterest.blister;
    if (!blister) return null;

    // Hidden-field check
    if (blisterFieldConfig?.[key]?.hidden) return null;

    // pdrHeight: also skip when there is no value
    if (key === 'pdrHeight' && !blister.pdrHeight) return null;

    const getLabel = (defaultLabel: string): string =>
      blisterFieldConfig?.[key]?.name || defaultLabel;

    let value: string;

    // maxHeight — special handling for 0 / uncertain profile
    if (key === 'maxHeight') {
      const defaultLabel =
        pointOfInterest.type === BLISTER_TYPE
          ? BLISTER_HEIGHT_LABEL
          : PIT_DEPTH_LABEL;
      const label = getLabel(defaultLabel);

      if (blister.maxHeight === 0) {
        return { label, value: UNCERTAIN_PROFILE_LABEL };
      }
      return {
        label,
        value:
          localiseMeasurement({
            unitSystem,
            value: blister.maxHeight || undefined,
            sourceUnit: UnitsEnum.Meter,
            displayAsUnit: UnitsEnum.Millimeter,
            fallbackLabel: UNCERTAIN_PROFILE_LABEL,
            showUnitLabel: true,
          }) ?? UNCERTAIN_PROFILE_LABEL,
      };
    }

    // Simple mm fields
    if (SIMPLE_MM_KEYS.has(key)) {
      value = formatMm(
        blister[key as keyof typeof blister] as number | null | undefined,
        unitSystem
      );
      // pdrHeight uncertain profile override
      if (key === 'pdrHeight' && blister.maxHeight === 0) {
        value = UNCERTAIN_PROFILE_LABEL;
      }
      return { label: getLabel(DEFAULT_BLISTER_LABELS[key] ?? key), value };
    }

    // mm + percentage fields (wallLoss, residualCorrosionAllowance)
    if (MM_PERCENTAGE_KEYS.has(key)) {
      const fieldData = blister[key as keyof typeof blister] as
        | { value?: number | null; percentage?: number | null }
        | null
        | undefined;
      value = formatMmWithPercentage(fieldData, unitSystem);
      return { label: getLabel(DEFAULT_BLISTER_LABELS[key] ?? key), value };
    }

    // residualThickness — mm only from compound (no percentage)
    if (key === 'residualThickness') {
      const rtValue = blister.residualThickness?.value;
      value =
        rtValue === null || rtValue === undefined
          ? NOT_APPLICABLE_LABEL
          : formatMm(rtValue, unitSystem, '');
      return { label: getLabel(DEFAULT_BLISTER_LABELS[key] ?? key), value };
    }

    // blisterHeightIndex — numeric to string
    if (key === 'blisterHeightIndex') {
      value =
        blister.blisterHeightIndex !== null &&
        blister.blisterHeightIndex !== undefined
          ? String(blister.blisterHeightIndex)
          : NOT_APPLICABLE_LABEL;
      return { label: getLabel(DEFAULT_BLISTER_LABELS[key] ?? key), value };
    }

    // String passthrough fields
    if (STRING_PASSTHROUGH_KEYS.has(key)) {
      const raw = blister[key as keyof typeof blister];
      value =
        raw !== null && raw !== undefined
          ? String(raw)
          : NOT_APPLICABLE_LABEL;
      return { label: getLabel(DEFAULT_BLISTER_LABELS[key] ?? key), value };
    }

    // Unknown blister key — skip
    return null;
  }

  // ----- PointOfInterest fields -----
  if (type === MarkerLabelFieldTypeEnum.PointOfInterest) {
    const accessor = POI_ACCESSORS[key];
    if (!accessor) return null;

    const label = POI_LABELS[key] ?? key;
    const value = accessor(pointOfInterest, unitSystem);
    return { label, value };
  }

  // TemplateField and unknown types are handled in the hook
  return null;
};

/**
 * Batch-resolves an array of review-panel fields (Blister / POI only).
 * Skips fields that return null.
 */
export const getReviewPanelFieldValues = (
  fields: { key: string; type: MarkerLabelFieldTypeEnum }[],
  pointOfInterest: PointOfInterest,
  unitSystem: UnitSystemEnum,
  blisterFieldConfig?: BlisterFieldConfig
): Array<{ label: string; value: string }> => {
  return fields.flatMap((field) => {
    const result = getReviewPanelFieldValue(
      field,
      pointOfInterest,
      unitSystem,
      blisterFieldConfig
    );
    return result ? [result] : [];
  });
};
